import React, { useState, useEffect, useMemo } from 'react';
import { all_routes } from '../../../router/all_routes';
import { Link } from 'react-router-dom';
import Table from "../../../../core/common/dataTable/index";
import CollapseHeader from '../../../../core/common/collapse-header/collapse-header';
import { API } from '../../../../common/Constants';
import useFetch from '../../../../hooks/useFetch';
import { getAPIHeaders } from '../../../../common/Utils'; 
import PreLoader from '../../../../core/common/preLoader';
import CopyRightInfo from '../../../../core/common/copyRightInfo';
import CustomToast from '../../../../core/common/customToast';
import PredefinedDateRanges from '../../../../core/common/datePicker';
import moment from 'moment';
import "../../../../style/css/shared.scss";

const Payments = () => {
  const { isLoading, error, callFetch } = useFetch();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "Something went wrong please try again later",
    heading: "Oops! Something went wrong",
    type: "ERROR",
  });
  const [viewType, setViewType] = useState<'table' | 'card'>('table');
  // Default date range: Past 1 year from current date
  const [dateRange, setDateRange] = useState<any>({
    start: moment().subtract(1, 'year').startOf('day').toDate().getTime(),
    end: moment().endOf('day').toDate().getTime(),
  });

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) {
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: error?.response?.data || "Something went wrong, please try again later.",
      }));
    }
  }, [error]);

  const fetchTransactions = () => {
    callFetch(
      `${API.PURCHASE_TRANSACTION}`,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((response) => {
      setTransactions(response || []);
    }).catch((err) => {
      console.error('Error fetching transactions:', err);
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: "Failed to fetch transactions. Please try again.",
        heading: "Error",
        type: "ERROR",
      }));
    });
  };

  const formatDate = (dateString: string | number) => {
    if (!dateString) return '-';
    let date: Date;
    if (typeof dateString === 'number') {
      date = new Date(dateString);
    } else {
      date = new Date(dateString);
    }
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const parseEntityDetails = (entityDetails: string) => {
    if (!entityDetails) return null;
    try {
      return JSON.parse(entityDetails);
    } catch (e) {
      return null;
    }
  };

  // Filter transactions based on date range
  const filteredTransactions = useMemo(() => {
    if (!transactions || transactions.length === 0) return [];
    
    return transactions.filter((transaction: any) => {
      let transactionDate: number;
      
      // Try to get date from paymentDate first (string format: "2025-11-15 22:32:20")
      if (transaction.paymentDate) {
        transactionDate = new Date(transaction.paymentDate).getTime();
      } 
      // Fall back to transactionCreatedOn (timestamp)
      else if (transaction.transactionCreatedOn) {
        transactionDate = transaction.transactionCreatedOn;
      } 
      // If neither exists, exclude from results
      else {
        return false;
      }
      
      // Check if transaction date is within the selected range
      return transactionDate >= dateRange.start && transactionDate <= dateRange.end;
    });
  }, [transactions, dateRange]);

  const formatCurrency = (amount: number, currencyCode: string = 'INR') => {
    if (amount === null || amount === undefined) return '-';
    const symbol = currencyCode === 'INR' ? '₹' : currencyCode;
    return `${symbol} ${amount.toLocaleString()}`;
  };

  const getStatusBadge = (status: string) => {
    const statusUpper = status?.toUpperCase();
    switch (statusUpper) {
      case 'SUCCESS':
      case 'PAID':
      case 'COMPLETED':
        return 'badge-soft-success';
      case 'PENDING':
      case 'PROCESSING':
        return 'badge-soft-warning';
      case 'FAILED':
      case 'CANCELLED':
      case 'REFUNDED':
        return 'badge-soft-danger';
      default:
        return 'badge-soft-secondary';
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    const methodUpper = method?.toUpperCase();
    switch (methodUpper) {
      case 'CREDIT_CARD':
        return 'ti ti-credit-card';
      case 'DEBIT_CARD':
        return 'ti ti-credit-card';
      case 'NETBANKING':
        return 'ti ti-building-bank';
      case 'UPI':
        return 'ti ti-device-mobile';
      case 'WALLET':
        return 'ti ti-wallet';
      default:
        return 'ti ti-currency-rupee';
    }
  };

  const columns = [
    {
      title: "Order ID",
      dataIndex: "orderId",
      render: (text: number, record: any) => (
        <div>
            <h6 className="fw-medium">
                {record.orderId || `TXN-${text}` || '-'}
            </h6>
        </div>
      ),
      sorter: (a: any, b: any) => (a.transactionId || 0) - (b.transactionId || 0),
      width: "12%",
    },
    {
      title: "Customer",
      dataIndex: "firstName",
      render: (firstName: string, record: any) => {
        const fullName = `${record.firstName || ''} ${record.lastName || ''}`.trim() || record.username || 'N/A';
        const username = record.username || 'N/A';
        return (
          <div className="d-flex align-items-center">
            <div className="avatar avatar-sm me-2 flex-shrink-0 bg-light-primary rounded-circle">
              <span className="text-primary fw-bold">
                {fullName?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div>
              <h6 className="fw-medium mb-0">{fullName}</h6>
              <p className="text-muted mb-0 small">{username}</p>
            </div>
          </div>
        );
      },
      width: "20%",
    },
    {
      title: "Subscription Plan",
      dataIndex: "entityDetails",
      render: (entityDetails: string, record: any) => {
        const entityInfo = parseEntityDetails(entityDetails);
        const planName = entityInfo?.entity_name || 'N/A';
        const planType = entityInfo?.plan_type || '';
        return (
          <div>
            <h6 className="fw-medium mb-0">{planName}</h6>
            {planType && (
              <span className={`badge ${planType === 'ENTERPRISE' ? 'badge-soft-danger' : planType === 'PREMIUM' ? 'badge-soft-success' : planType === 'BASIC' ? 'badge-soft-info' : 'badge-soft-secondary'}`}>
                {planType}
              </span>
            )}
          </div>
        );
      },
      width: "18%",
    },
    {
      title: "Amount",
      dataIndex: "transactionAmount",
      render: (amount: number, record: any) => {
        const transactionAmount = amount || 0;
        const currency = record.transactionCurrency || 'INR';
        return (
          <div className="d-flex flex-column">
            <h6 className="fw-medium mb-0 text-primary">
             <span className="text-muted">Paid:</span> {formatCurrency(transactionAmount, currency)}
            </h6>
            {record.orderPromotionalDiscount > 0 && (
              <small className="text-muted">
                <span className="text-muted">Order Price:</span>  {formatCurrency(record.orderOriginalAmount, currency)}
              </small>
            )}
            {record.orderPromotionalDiscount > 0 && (
              <small className="text-muted">
                <span className="text-muted">Discount:</span>  {formatCurrency(record.orderPromotionalDiscount, currency)}
              </small>
            )}
            {record.taxAmount > 0 && (
              <small className="ms-1 text-muted">
                <span className="text-muted">Tax:</span>  {formatCurrency(record.taxAmount, currency)}
              </small>
            )}
          </div>
        );
      },
      sorter: (a: any, b: any) => (a.transactionAmount || 0) - (b.transactionAmount || 0),
      width: "12%",
      align: "center" as "center",
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      render: (method: string, record: any) => {
        const paymentMethod = method || 'N/A';
        return (
          <div className="d-flex align-items-center justify-content-center">
            <i className={`${getPaymentMethodIcon(paymentMethod)} me-2`}></i>
            <span className="text-capitalize">
              {paymentMethod?.replace('_', ' ') || 'N/A'}
            </span>
          </div>
        );
      },
      width: "12%",
      align: "center" as "center",
    },
    {
      title: "Status",
      dataIndex: "transactionStatus",
      render: (status: string, record: any) => {
        const transactionStatus = status || 'PENDING';
        return (
          <span className={`badge ${getStatusBadge(transactionStatus)} d-flex align-items-center justify-content-center`}>
            <i className="ti ti-circle-filled fs-8 me-1"></i>
            {transactionStatus}
          </span>
        );
      },
      width: "12%",
      align: "center" as "center",
    },
    {
      title: "Transaction Date",
      dataIndex: "paymentDate",
      render: (date: string, record: any) => {
        const transactionDate = date || record.transactionCreatedOn;
        return (
          <div className="text-center">
            <div className="fw-medium">{formatDate(transactionDate)}</div>
          </div>
        );
      },
      sorter: (a: any, b: any) => {
        const dateA = a.paymentDate ? new Date(a.paymentDate).getTime() : (a.transactionCreatedOn || 0);
        const dateB = b.paymentDate ? new Date(b.paymentDate).getTime() : (b.transactionCreatedOn || 0);
        return dateA - dateB;
      },
      width: "14%",
      align: "center" as "center",
    },
  ];

  return (
    <>
      <PreLoader showLoader={isLoading} />
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Order & Payments</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Online Learning Center</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Payments
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Transaction List</h5>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="view-toggle me-3">
                  <button 
                    className={`btn btn-sm me-2 ${viewType === 'table' ? 'btn-primary' : 'btn-outline-primary'}`} 
                    onClick={() => setViewType('table')}
                    aria-label="Switch to table view"
                  >
                    <i className="ti ti-table me-1"></i> Table View
                  </button>
                  <button 
                    className={`btn btn-sm ${viewType === 'card' ? 'btn-primary' : 'btn-outline-primary'}`} 
                    onClick={() => setViewType('card')}
                    aria-label="Switch to card view"
                  >
                    <i className="ti ti-layout-grid me-1"></i> Card View
                  </button>
                </div>
                <div className="me-3">
                  <div className="input-icon-end position-relative">
                    <PredefinedDateRanges 
                      onChange={(start: moment.Moment, end: moment.Moment) => {
                        setDateRange({
                          start: start?.toDate().getTime(),
                          end: end?.toDate().getTime()
                        });
                      }}
                    />
                    <span className="input-icon-addon">
                      <i className="ti ti-chevron-down" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              {viewType === 'table' ? (
                <Table
                  dataSource={filteredTransactions}
                  columns={columns}
                  Selection={false}
                  refresh={fetchTransactions}
                />
              ) : (
                <div className="transaction-cards p-3">
                  <div className="row">
                    {filteredTransactions.map((transaction: any) => {
                      const fullName = `${transaction.firstName || ''} ${transaction.lastName || ''}`.trim() || transaction.username || 'N/A';
                      const userEmail = transaction.email || 'N/A';
                      const entityInfo = parseEntityDetails(transaction.entityDetails);
                      const planName = entityInfo?.entity_name || 'N/A';
                      const planType = entityInfo?.plan_type || '';
                      const amount = transaction.transactionAmount || 0;
                      const currency = transaction.transactionCurrency || 'INR';
                      const status = transaction.transactionStatus || 'PENDING';
                      const method = transaction.paymentMethod || 'N/A';
                      const date = transaction.paymentDate || transaction.transactionCreatedOn;
                      
                      return (
                        <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={transaction.id || transaction.transactionId}>
                          <div className="card h-100 border shadow-hover transition-all">
                            <div className="card-body position-relative">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center">
                                  <div className="avatar avatar-sm me-3 flex-shrink-0 bg-light-primary rounded-circle">
                                    <span className="text-primary fw-bold">
                                      {fullName?.charAt(0)?.toUpperCase() || 'U'}
                                    </span>
                                  </div>
                                  <div>
                                    <h6 className="mb-0 fw-semibold">{fullName}</h6>
                                    <small className="text-muted">{userEmail}</small>
                                  </div>
                                </div>
                                <span className={`badge ${getStatusBadge(status)}`}>
                                  {status}
                                </span>
                              </div>
                              
                              <div className="mb-3">
                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fs-12 fw-medium text-dark">Order ID</div>
                                  <span className="badge badge-soft-info">
                                    {transaction.orderId || `TXN-${transaction.transactionId}` || 'N/A'}
                                  </span>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fs-12 fw-medium text-dark">Subscription Plan</div>
                                  <div className="text-end">
                                    <div className="fw-semibold text-primary">{planName}</div>
                                    {planType && (
                                      <span className={`badge ${planType === 'ENTERPRISE' ? 'badge-soft-danger' : planType === 'PREMIUM' ? 'badge-soft-success' : 'badge-soft-info'}`}>
                                        {planType}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fs-12 fw-medium text-dark">Amount</div>
                                  <div className="text-end d-flex flex-column">
                                    <span className="fw-semibold text-primary">
                                      {formatCurrency(amount, currency)}
                                    </span>
                                    {transaction.orderPromotionalDiscount > 0 && (
                                      <div>
                                        <small className="text-muted">
                                          <span className="text-muted">Order Price:</span>  {formatCurrency(transaction.orderOriginalAmount || amount, currency)}
                                        </small>
                                      </div>
                                    )}
                                    {transaction.orderPromotionalDiscount > 0 && (
                                      <small className="text-muted">
                                        <span className="text-muted">Discount:</span>  {formatCurrency(transaction.orderPromotionalDiscount, currency)}
                                      </small>
                                    )}
                                    {transaction.taxAmount > 0 && (
                                      <small className="ms-1 text-muted">
                                        <span className="text-muted">Tax:</span>  {formatCurrency(transaction.taxAmount, currency)}
                                      </small>
                                    )}
                                  </div>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fs-12 fw-medium text-dark">Payment Method</div>
                                  <div className="d-flex align-items-center">
                                    <i className={`${getPaymentMethodIcon(method)} me-1`}></i>
                                    <span className="text-capitalize">{method?.replace('_', ' ')}</span>
                                  </div>
                                </div>

                                <div className="d-flex justify-content-between">
                                  <div className="fs-12 fw-medium text-dark">Date</div>
                                  <span className="text-muted">{formatDate(date)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {filteredTransactions.length === 0 && (
                      <div className="col-12 text-center py-5">
                        <div className="empty-state">
                          <i className="ti ti-search-off fs-1 text-muted mb-3"></i>
                          <h5 className="mb-2">No transactions found</h5>
                          <p className="text-muted">
                            {transactions.length === 0 
                              ? "No payment transactions available."
                              : "No payment transactions match your current date filter criteria."}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* /Transaction list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo />
        </div>
      </div>
    </>
  );
};

export default Payments;

